import React, { useRef, useCallback } from "react";

import { formatSsn } from "../utils/formatting";


/**
 * Custom hook to manage SSN input masking and cursor position restoration.
 *
 * @param {string} previousSsn - The current value of the SSN field (used for comparison).
 * @param {(value: string) => void} setSsn - A stable callback function to update the SSN field's state.
 * @returns {[React.RefObject<HTMLInputElement>, function(React.ChangeEvent<HTMLInputElement>): void]}
 * An array containing the input ref and the stable onChange handler.
 */
export const useSsnInputMask = (
  previousSsn: string,
  setSsn: (value: string) => void,
): [
  React.Ref<HTMLInputElement>,
  (e: React.ChangeEvent<HTMLInputElement>) => void,
] => {

  const inputRef = useRef<HTMLInputElement>(null);

  // The handler is memoized using useCallback
  const handleSsnChange = useCallback((e:  React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    let newCursorPosition = e.target.selectionStart || 0;

    const oldDigits = previousSsn.replace(/\D/g, '');
    const newDigits = rawValue.replace(/\D/g, '');
    const newDigitsLength = newDigits.length;
    const oldDigitsLength = oldDigits.length;

    let finalDigits = newDigits;

    // --- Backspace Edge Case Detection ---
    const isDeleting = rawValue.length < previousSsn.length;
    const isDashDeletion = isDeleting && newDigitsLength === oldDigitsLength;

    if (isDashDeletion) {
      // newCursorPosition is where the cursor landed *after* the dash was removed by the browser.
      const indexToDelete = newCursorPosition - 1; // Index in the digits string to remove (e.g., if cursor is at 4, indexToDelete is 3)

      // 1. Manually construct the new digits string by omitting the intended digit.
      finalDigits = newDigits.substring(0, indexToDelete) + newDigits.substring(indexToDelete + 1);

      // 2. The cursor naturally moves back by one position because a digit was deleted.
      newCursorPosition--;
    } else if (newDigitsLength < oldDigitsLength) {
      // Standard digit deletion
      if (newCursorPosition === 4 || newCursorPosition === 7) {
        newCursorPosition--;
      }
    } else if (newDigitsLength > oldDigitsLength) {
      // Standard typing (digit addition)
      if (newCursorPosition === 4 || newCursorPosition === 7) {
        newCursorPosition++;
      }
    }

    // Apply final formatting and state update
    const newFormattedSsn = formatSsn(finalDigits);
    setSsn(newFormattedSsn);

    // Restore cursor position
    requestAnimationFrame(() => {
      if (inputRef.current) {
        // Ensure cursor doesn't go past the end of the new string
        const finalPos = Math.min(newCursorPosition, newFormattedSsn.length);
        inputRef.current.setSelectionRange(finalPos, finalPos);
      }
    });
  }, [previousSsn, setSsn]);

  return [inputRef, handleSsnChange];
};
